import { makeAutoObservable } from 'mobx';
import {
  IPatientNotesStore,
  ITimeSettings,
  INoteTrend,
  ParameterType,
  GaitParamType,
  FallParamType,
  INoteTable,
  DataTableType,
  INoteHeader,
} from './types';
import { Dictionary, set } from 'lodash';
import { INoteTemplateDocument } from './templateService/types';
import { AggregatedPeriod } from '../../Pages/SinglePatientDetails/PatientPages/PatientNotes/types';
import { CareLogFiltersType } from '../careLogService/types';

export class PatientNotesStore implements IPatientNotesStore {
  note: INoteTemplateDocument | Record<string, never> = {};
  timeSettings: ITimeSettings = {
    agg: {
      binSize: AggregatedPeriod.Weekly,
      binCount: 5,
    },
    showTimeSpan: false,
    perceptions: [],
    questionnaires: [],
    measurementTags: [],
  };
  filters: { [id: string]: CareLogFiltersType } = {};
  headers: Dictionary<INoteHeader> = {};
  trends: Dictionary<INoteTrend> = {};
  tables: Dictionary<INoteTable> = {};
  debouncing = false;
  isDirty = false;

  constructor(defaultValues: { [id: string]: CareLogFiltersType } = {}) {
    makeAutoObservable(this);
    this.filters = defaultValues;
  }

  setNote(
    notes: INoteTemplateDocument | Record<string, never>,
    debouncing?: boolean,
    isMount?: boolean
  ) {
    if(!isMount) {
      this.isDirty = true;
    }

    this.debouncing = !!debouncing;
    this.note = notes ?? {};
  }

  updateNoteProperty(
    path: string,
    value:
      | INoteTemplateDocument[keyof INoteTemplateDocument]
      | string
      | number
      | boolean
      | null,
    debouncing?: boolean
  ) {
    this.isDirty = true;
    this.debouncing = !!debouncing;
    set(this.note, path, value);
  }

  setTimeSettings(settings: ITimeSettings) {
    this.timeSettings = settings;
  }

  resetSettings() {
    this.note = {}
    this.timeSettings = {
      agg: {
        binSize: AggregatedPeriod.Weekly,
        binCount: 5,
      },
      showTimeSpan: false,
      perceptions: [],
      measurementTags: [],
    };
    this.filters = {};
    this.isDirty = false;
  }

  setFilter(key: string, value: CareLogFiltersType) {
    this.filters[key] = value;
  }

  setHeader(id: string, value: INoteHeader) {
    this.headers[id] = value;
  }

  fillHeaders(dictionary: Dictionary<INoteHeader>) {
    this.headers = dictionary;
  }

  fillTrends(dictionary: Dictionary<INoteTrend>) {
    this.trends = dictionary;
  }

  setTrend(
    id: ParameterType | GaitParamType | FallParamType,
    key: keyof INoteTrend,
    value: INoteTrend[keyof INoteTrend]
  ) {
    this.trends[id] = {
      ...this.trends[id],
      [key]: value,
    };
  }

  fillTables(dictionary: Dictionary<INoteTable>) {
    this.tables = dictionary;
  }

  setTable(
    id: DataTableType,
    key: keyof INoteTable,
    value: INoteTable[keyof INoteTable]
  ) {
    this.tables[id] = {
      ...this.tables[id],
      [key]: value,
    };
  }
}
